// ═══════════════════════════════════════════════════
// Atmosphere — Night Sky Engine for the Dashboard
// Milky way band, 3 star layers, shooting stars,
// drifting clouds, CSS nebulae
// Ported from the landing page, tuned for daily use
// ═══════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import styles from './Atmosphere.module.css';

// Dashboard version: slightly reduced intensity for daily use
const INTENSITY = 1.0;

interface Star {
  x: number; y: number; r: number;
  a: number; da: number; min: number; max: number;
  glow: boolean; hue: number; hasTint: boolean;
}

interface ShootingStar {
  x: number; y: number;
  vx: number; vy: number;
  life: number; len: number;
}

export function Atmosphere() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const maybeCtx = canvas.getContext('2d');
    if (!maybeCtx) return;

    // Assign to new const so closures see the narrowed non-null type
    const c = canvas;
    const ctx = maybeCtx;

    let W = 0, H = 0;
    let mwCanvas: HTMLCanvasElement | null = null;
    let stars: Star[] = [];
    let cloudOffset = 0;
    let shootingStar: ShootingStar | null = null;
    let shootingTimer = 0;
    let frameCount = 0;

    // ── Noise ────────────────────────────────────
    function hash(x: number, y: number): number {
      let h = x * 374761393 + y * 668265263;
      h = (h ^ (h >> 13)) * 1274126177;
      return (h ^ (h >> 16)) & 0x7fffffff;
    }

    function noise2d(x: number, y: number): number {
      const ix = Math.floor(x), iy = Math.floor(y);
      const fx = x - ix, fy = y - iy;
      const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
      const a = hash(ix, iy) / 0x7fffffff;
      const b = hash(ix + 1, iy) / 0x7fffffff;
      const c = hash(ix, iy + 1) / 0x7fffffff;
      const d = hash(ix + 1, iy + 1) / 0x7fffffff;
      return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
    }

    function fbm(x: number, y: number, o: number): number {
      let v = 0, amp = 0.5, freq = 1;
      for (let i = 0; i < o; i++) {
        v += amp * noise2d(x * freq, y * freq);
        amp *= 0.5; freq *= 2;
      }
      return v;
    }

    // ── Milky Way ────────────────────────────────
    function paintMilkyWay() {
      mwCanvas = document.createElement('canvas');
      mwCanvas.width = W;
      mwCanvas.height = H;
      const mc = mwCanvas.getContext('2d')!;
      const angle = -0.35;
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      for (let px = 0; px < W; px += 3) {
        for (let py = 0; py < H; py += 3) {
          const ry = (px - W * 0.5) * sinA + (py - H * 0.5) * cosA;
          const bandDist = Math.abs(ry) / (H * 0.28);
          const bandStrength = Math.exp(-bandDist * bandDist * 2.5);
          const n = fbm(px * 0.002, py * 0.002, 4);
          const density = bandStrength * n;
          if (density > 0.04) {
            const hue = 220 + n * 60;
            const alpha = Math.min(density * 0.28, 0.12) * INTENSITY;
            mc.fillStyle = `hsla(${hue},40%,65%,${alpha})`;
            mc.fillRect(px, py, 3, 3);
          }
        }
      }
    }

    // ── Stars ────────────────────────────────────
    function initStars() {
      stars = [];
      const angle = -0.35;
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const layers = [
        { count: Math.floor(W * H / 3200), rMin: 0.3, rMax: 0.8, aMin: 0.03, aMax: 0.2, glow: false },
        { count: Math.floor(W * H / 8000), rMin: 0.6, rMax: 1.2, aMin: 0.08, aMax: 0.35, glow: false },
        { count: Math.floor(W * H / 25000), rMin: 1.0, rMax: 2.2, aMin: 0.2, aMax: 0.7, glow: true },
      ];
      for (const L of layers) {
        for (let i = 0; i < L.count; i++) {
          let x = Math.random() * W, y = Math.random() * H;
          const ry = (x - W * 0.5) * sinA + (y - H * 0.5) * cosA;
          const bandDist = Math.abs(ry) / (H * 0.28);
          if (bandDist > 1.2 && Math.random() < 0.4) {
            x = Math.random() * W;
            y = Math.random() * H;
          }
          const hue = Math.random() < 0.15 ? (200 + Math.random() * 80) : (Math.random() < 0.1 ? (30 + Math.random() * 20) : 0);
          stars.push({
            x, y,
            r: L.rMin + Math.random() * (L.rMax - L.rMin),
            a: L.aMin + Math.random() * (L.aMax - L.aMin) * 0.5,
            da: (Math.random() * 0.004 + 0.001) * (Math.random() < 0.5 ? 1 : -1),
            min: L.aMin, max: L.aMax,
            glow: L.glow, hue, hasTint: hue !== 0,
          });
        }
      }
    }

    // ── Clouds ───────────────────────────────────
    function drawClouds() {
      cloudOffset += 0.08;
      const step = 6;
      for (let px = 0; px < W; px += step) {
        for (let py = 0; py < H; py += step) {
          const n = fbm((px + cloudOffset) * 0.0008, py * 0.0012, 3);
          const n2 = fbm((px + cloudOffset * 0.6) * 0.0015, (py + 100) * 0.001, 2);
          const combined = n * 0.6 + n2 * 0.4;
          if (combined > 0.42) {
            const alpha = Math.min((combined - 0.42) * 0.16, 0.05) * INTENSITY;
            ctx.fillStyle = `rgba(180,190,220,${alpha})`;
            ctx.fillRect(px, py, step, step);
          }
        }
      }
    }

    // ── Shooting Stars ───────────────────────────
    function maybeShootingStar() {
      shootingTimer++;
      if (!shootingStar && shootingTimer > 600 && Math.random() < 0.003) {
        shootingStar = {
          x: Math.random() * W * 0.8, y: Math.random() * H * 0.4,
          vx: 3 + Math.random() * 4, vy: 1.5 + Math.random() * 2,
          life: 1, len: 40 + Math.random() * 60,
        };
        shootingTimer = 0;
      }
      if (shootingStar) {
        const s = shootingStar;
        s.x += s.vx; s.y += s.vy; s.life -= 0.015;
        if (s.life <= 0) { shootingStar = null; return; }
        const grad = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * s.len * 0.15, s.y - s.vy * s.len * 0.15);
        grad.addColorStop(0, `rgba(255,255,255,${s.life * 0.9})`);
        grad.addColorStop(0.3, `rgba(200,220,255,${s.life * 0.4})`);
        grad.addColorStop(1, 'rgba(200,220,255,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - s.vx * s.len * 0.15, s.y - s.vy * s.len * 0.15);
        ctx.stroke();
      }
    }

    // ── Draw Loop ────────────────────────────────
    function draw() {
      ctx.clearRect(0, 0, W, H);
      if (mwCanvas) ctx.drawImage(mwCanvas, 0, 0);
      if (frameCount % 3 === 0) drawClouds();

      for (const s of stars) {
        s.a += s.da;
        if (s.a > s.max || s.a < s.min) s.da *= -1;
        s.a = Math.max(s.min, Math.min(s.max, s.a));
        const intensity = s.a * INTENSITY;

        // Glow halos + sparkle cross for close stars
        if (s.glow && intensity > 0.3 * INTENSITY) {
          const glowR = s.r * 4;
          const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, glowR);
          if (s.hasTint) {
            grad.addColorStop(0, `hsla(${s.hue},50%,88%,${intensity * 0.6})`);
            grad.addColorStop(0.3, `hsla(${s.hue},40%,75%,${intensity * 0.2})`);
          } else {
            grad.addColorStop(0, `rgba(230,235,255,${intensity * 0.6})`);
            grad.addColorStop(0.3, `rgba(210,220,255,${intensity * 0.2})`);
          }
          grad.addColorStop(1, 'rgba(200,210,255,0)');
          ctx.beginPath();
          ctx.arc(s.x, s.y, glowR, 0, Math.PI * 2);
          ctx.fillStyle = grad;
          ctx.fill();

          // 4-point sparkle cross
          ctx.save();
          ctx.globalAlpha = intensity * 0.8;
          ctx.strokeStyle = s.hasTint ? `hsla(${s.hue},30%,92%,${intensity})` : `rgba(240,245,255,${intensity})`;
          ctx.lineWidth = 0.5;
          const spLen = s.r * 3.5 * intensity;
          ctx.beginPath(); ctx.moveTo(s.x - spLen, s.y); ctx.lineTo(s.x + spLen, s.y); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(s.x, s.y - spLen); ctx.lineTo(s.x, s.y + spLen); ctx.stroke();
          ctx.restore();
        }

        // Star dot
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * (s.glow ? 0.7 : 1), 0, Math.PI * 2);
        ctx.fillStyle = s.hasTint ? `hsla(${s.hue},30%,90%,${intensity})` : `rgba(200,210,255,${intensity})`;
        ctx.fill();
      }

      maybeShootingStar();
      frameCount++;
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Init ─────────────────────────────────────
    function resize() {
      W = c.width = window.innerWidth;
      H = c.height = window.innerHeight;
      paintMilkyWay();
      initStars();
    }

    resize();
    draw();
    window.addEventListener('resize', resize, { passive: true });

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className={styles.atmosphere} aria-hidden="true">
      <canvas ref={canvasRef} className={styles.canvas} />
      <div className={`${styles.nebula} ${styles.nb1}`} />
      <div className={`${styles.nebula} ${styles.nb2}`} />
      <div className={`${styles.nebula} ${styles.nb3}`} />
      <div className={`${styles.nebula} ${styles.nb4}`} />
    </div>
  );
}

